/**
 * Puzzle: one of your own pictures, cut up and put back together.
 *
 * Using the child's own drawing is what makes it worth doing — it is not a
 * stock photograph of a kitten, it is the rocket they drew on Tuesday. Pieces
 * snap home when they are close enough, because a five-year-old's aim is not
 * the skill being practised here.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, Images, RotateCcw, Shuffle } from 'lucide-preact';
import type { Vec } from '../../engine/vec.ts';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { renderScene } from '../../engine/snapshot.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play, playNote } from '../../engine/audio.ts';
import type { Scene } from '../../lib/scene.ts';
import type { Piece, Puzzle } from '../../lib/jigsaw.ts';
import { isSolved, makePuzzle, scatterSpot } from '../../lib/jigsaw.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { PicturePicker } from '../../ui/PicturePicker.tsx';
import { Coach, Stepper, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const TOP = 148;
const BOTTOM = 84;
/** How close counts as home, as a fraction of a piece. */
const SNAP = 0.34;

interface Held {
  index: number;
  grab: Vec;
}

export function PuzzleToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const clockRef = useRef(0);
  /** The picture being puzzled, drawn once onto its own canvas. */
  const imageRef = useRef<HTMLCanvasElement | null>(null);
  const puzzleRef = useRef<Puzzle>(makePuzzle(3, 3, 1));
  /** Where each piece currently sits, in fractions of the board. */
  const spotsRef = useRef<Vec[]>([]);
  const placedRef = useRef<boolean[]>([]);
  const heldRef = useRef<Held | null>(null);
  const boardRef = useRef({ x: 0, y: 0, w: 1, h: 1, cell: 1 });

  const [size, setSize] = useState(3);
  const [done, setDone] = useState(0);
  const [picking, setPicking] = useState(false);
  const [hasPicture, setHasPicture] = useState(false);

  const total = size * size;

  /** Draw a picture we can cut up even before the child has kept any. */
  const defaultPicture = useCallback((): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    ctx.fillStyle = token('--paper');
    ctx.fillRect(0, 0, 600, 600);
    const colors = ['--crayon-red', '--crayon-yellow', '--crayon-teal', '--crayon-sky', '--crayon-purple', '--crayon-orange'];
    // Overlapping crayon circles: busy enough that every piece is distinct.
    for (let i = 0; i < 9; i++) {
      ctx.beginPath();
      ctx.arc(90 + (i % 3) * 210, 90 + Math.floor(i / 3) * 210, 96, 0, Math.PI * 2);
      ctx.fillStyle = token(colors[i % colors.length]);
      ctx.globalAlpha = 0.9;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.lineWidth = 8;
      ctx.strokeStyle = 'rgba(44, 37, 69, 0.8)';
      ctx.stroke();
    }
    return canvas;
  }, []);

  const shuffle = useCallback((n = size) => {
    const seed = Math.floor(Math.random() * 100000);
    puzzleRef.current = makePuzzle(n, n, seed);
    spotsRef.current = puzzleRef.current.pieces.map((_, i) => scatterSpot(i, n * n, seed));
    placedRef.current = puzzleRef.current.pieces.map(() => false);
    heldRef.current = null;
    setDone(0);
  }, [size]);

  useEffect(() => {
    if (!imageRef.current) imageRef.current = defaultPicture();
    shuffle(size);
  }, [size, shuffle, defaultPicture]);

  const usePicture = (scene: Scene) => {
    const shot = renderScene(scene, 600, token('--paper'));
    if (shot) {
      imageRef.current = shot.canvas;
      setHasPicture(true);
    }
    setPicking(false);
    shuffle(size);
  };

  /** The outline of one piece, tabs and all, in board pixels. */
  const tracePiece = (ctx: CanvasRenderingContext2D, piece: Piece, x: number, y: number, cell: number) => {
    const tab = cell * 0.2;
    const a = cell * 0.35;
    const b = cell * 0.65;
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Top
    if (piece.top === 0) ctx.lineTo(x + cell, y);
    else {
      ctx.lineTo(x + a, y);
      ctx.bezierCurveTo(x + a, y - piece.top * tab, x + b, y - piece.top * tab, x + b, y);
      ctx.lineTo(x + cell, y);
    }
    // Right
    if (piece.right === 0) ctx.lineTo(x + cell, y + cell);
    else {
      ctx.lineTo(x + cell, y + a);
      ctx.bezierCurveTo(x + cell + piece.right * tab, y + a, x + cell + piece.right * tab, y + b, x + cell, y + b);
      ctx.lineTo(x + cell, y + cell);
    }
    // Bottom
    if (piece.bottom === 0) ctx.lineTo(x, y + cell);
    else {
      ctx.lineTo(x + b, y + cell);
      ctx.bezierCurveTo(x + b, y + cell + piece.bottom * tab, x + a, y + cell + piece.bottom * tab, x + a, y + cell);
      ctx.lineTo(x, y + cell);
    }
    // Left
    if (piece.left === 0) ctx.closePath();
    else {
      ctx.lineTo(x, y + b);
      ctx.bezierCurveTo(x - piece.left * tab, y + b, x - piece.left * tab, y + a, x, y + a);
      ctx.closePath();
    }
  };

  const homeOf = (piece: Piece): Vec => ({
    x: (piece.col + 0.5) / puzzleRef.current.cols,
    y: (piece.row + 0.5) / puzzleRef.current.rows,
  });

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      clockRef.current += dt;
      updateSparks(sparksRef.current, dt);
      const puzzle = puzzleRef.current;
      const image = imageRef.current;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // The board takes the top half; the rest is the tray the pieces start in,
      // which has to be big enough to lay them all out without a heap.
      const usableH = height - TOP - BOTTOM;
      const board = Math.min(width * 0.8, usableH * 0.56);
      const bx = width / 2 - board / 2;
      const by = TOP;
      const cell = board / puzzle.cols;
      boardRef.current = { x: bx, y: by, w: board, h: board, cell };

      // Where the picture goes, so there is somewhere to aim at.
      ctx.save();
      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 7]);
      ctx.strokeRect(bx, by, board, board);
      ctx.restore();

      if (!image) return;

      // Unplaced pieces last, so the one being dragged is on top.
      const order = puzzle.pieces
        .map((piece, i) => ({ piece, i }))
        .sort((p, q) => Number(placedRef.current[q.i]) - Number(placedRef.current[p.i]));

      for (const { piece, i } of order) {
        const spot = spotsRef.current[i];
        if (!spot) continue;
        const held = heldRef.current?.index === i;
        // Board fractions to screen: placed pieces sit exactly on their square.
        const cx = bx + spot.x * board;
        const cy = by + spot.y * board;
        const x = cx - cell / 2;
        const y = cy - cell / 2;

        ctx.save();
        if (held) {
          ctx.shadowColor = 'rgba(44, 37, 69, 0.35)';
          ctx.shadowBlur = 18;
          ctx.shadowOffsetY = 6;
        }
        tracePiece(ctx, piece, x, y, cell);
        ctx.save();
        ctx.clip();
        // The matching square of the picture, drawn through the piece's hole.
        const sw = image.width / puzzle.cols;
        const sh = image.height / puzzle.rows;
        ctx.drawImage(
          image,
          piece.col * sw - sw * 0.25,
          piece.row * sh - sh * 0.25,
          sw * 1.5,
          sh * 1.5,
          x - cell * 0.25,
          y - cell * 0.25,
          cell * 1.5,
          cell * 1.5
        );
        ctx.restore();
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = placedRef.current[i] ? 'rgba(44, 37, 69, 0.45)' : 'rgba(44, 37, 69, 0.85)';
        ctx.stroke();
        ctx.restore();
      }

      drawSparks(ctx, sparksRef.current);
    },

    onDown: (p) => {
      const { x: bx, y: by, w: board, cell } = boardRef.current;
      // Topmost unplaced piece under the finger. Placed ones stay put.
      for (let i = puzzleRef.current.pieces.length - 1; i >= 0; i--) {
        if (placedRef.current[i]) continue;
        const spot = spotsRef.current[i];
        if (!spot) continue;
        const cx = bx + spot.x * board;
        const cy = by + spot.y * board;
        if (Math.abs(p.pos.x - cx) <= cell / 2 && Math.abs(p.pos.y - cy) <= cell / 2) {
          heldRef.current = { index: i, grab: { x: p.pos.x - cx, y: p.pos.y - cy } };
          play('pop', 0.4);
          return;
        }
      }
    },

    onMove: (p) => {
      const held = heldRef.current;
      if (!held) return;
      const { x: bx, y: by, w: board } = boardRef.current;
      spotsRef.current[held.index] = {
        x: (p.pos.x - held.grab.x - bx) / board,
        y: (p.pos.y - held.grab.y - by) / board,
      };
    },

    onUp: () => {
      const held = heldRef.current;
      heldRef.current = null;
      if (!held) return;

      const piece = puzzleRef.current.pieces[held.index];
      const spot = spotsRef.current[held.index];
      const home = homeOf(piece);
      const near = Math.hypot(spot.x - home.x, spot.y - home.y) * puzzleRef.current.cols;

      if (near < SNAP) {
        spotsRef.current[held.index] = home;
        placedRef.current[held.index] = true;
        const count = placedRef.current.filter(Boolean).length;
        setDone(count);
        playNote(Math.min(12, count), { duration: 0.3, gain: 0.22 });

        if (isSolved(placedRef.current)) {
          play('yay', 0.85);
          const rect = canvasRef.current?.getBoundingClientRect();
          if (rect) spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.4 }, partyColors(), 32, 460);
        }
      }
    },
  });

  const solved = done === total;

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Jigsaw puzzle" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton round label="Use one of my pictures" hideLabel onClick={() => setPicking(true)}>
              <Images size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Mix them up again" hideLabel onClick={() => shuffle()}>
              <Shuffle size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Start again" hideLabel onClick={() => shuffle()}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>
            {done} of {total}
          </strong>
          <span>{solved ? 'all done!' : 'pieces in place'}</span>
        </p>

        <Coach>
          {solved
            ? 'You put it all back together'
            : hasPicture
              ? 'Drag a piece near its place and it will snap in'
              : 'Tap the pictures button to puzzle your own drawing'}
        </Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row">
          <Stepper value={size} min={2} max={5} label="pieces across" onChange={setSize} />
        </div>
      </div>

      {picking && (
        <PicturePicker title="Which picture shall we cut up?" onPick={usePicture} onCancel={() => setPicking(false)} />
      )}
    </div>
  );
}
